// src/hooks/useFollowStatus.ts
//
// Is the signed-in user following this creator? Mirrors
// useSubscriptionStatus.ts's shape exactly, but against the newly-added
// follow endpoints (subscriptions/follow, /unfollow, /following) — a
// separate, free relationship from paid subscription status, which
// useSubscriptionStatus.ts still owns independently (the "Subscribed" tag
// on posts uses that one, unchanged).
//
// GET subscriptions/following?username= takes a username, not an email —
// unlike every other viewer-scoped query in this app, which key off
// userObject.email directly. There's no shortcut for "my own username" on
// the auth state itself (userObject only carries email/usid/role — see
// studio/live.tsx's identity-mismatch notes), so this fetches the viewer's
// own profile first to get it.
//
// Confirmed from a real payload capture: `data` is a flat array of plain
// EMAIL strings (e.g. ["damisco005@gmail.com"]) — not objects, and not
// usernames. That's what broke this the first time: the original version
// expected {username} / {creator:{username}} objects and compared against
// the target's USERNAME, so `f?.username` on a bare string was always
// undefined and the match always failed regardless of actual follow state.
// Since the list is email-keyed but every FollowBtn call site only has a
// username, this resolves the target creator's email via GET
// profile/{username} (same endpoint/shape LiveStreamPage.tsx and
// creator.tsx already rely on) before comparing.
import { useGetData } from "@/hooks/api/use-api";
import { useAppSelector } from "@/services/hook";
import type { RootState } from "@/services/store";
import { useFetchProfile } from "@/hooks/apiHooks";

export function useFollowStatus(creatorUsername: string) {
  const { userObject } = useAppSelector((s: RootState) => s.auth);
  const { data: myProfile } = useFetchProfile(userObject);
  const myUsername = (myProfile as { data?: { username?: string } } | undefined)
    ?.data?.username;

  const { data: creatorProfile } = useGetData({
    url: `profile/${creatorUsername}`,
    queryKey: ["GetProfileByUsername", creatorUsername],
    enabled: !!creatorUsername,
  });
  const creatorEmail = (
    creatorProfile as { data?: { email?: string } } | undefined
  )?.data?.email;

  const { data, isLoading } = useGetData({
    url: `subscriptions/following?username=${encodeURIComponent(myUsername ?? "")}`,
    queryKey: ["GetFollowing", myUsername],
    enabled: !!myUsername,
  });

  const payload = (data as { data?: unknown } | undefined)?.data;
  // Flat array of emails is the confirmed shape; {content:[...]} kept as a
  // defensive fallback in case this ever gets paginated.
  const following: unknown[] = Array.isArray(payload)
    ? payload
    : ((payload as { content?: unknown[] } | undefined)?.content ?? []);

  const isFollowing = following.some((f) => {
    if (typeof f === "string") return f === creatorEmail;
    const entry = f as { username?: string; email?: string; creator?: { username?: string; email?: string } };
    return (
      entry?.email === creatorEmail ||
      entry?.username === creatorUsername ||
      entry?.creator?.email === creatorEmail ||
      entry?.creator?.username === creatorUsername
    );
  });

  return { isFollowing, isLoading };
}
